/**
 * Обрезает целую часть числа до максимально возможной длины, сохраняя дробную часть неизменной.
 * Если длина целой части числа меньше или равна максимальной, число возвращается без изменений.
 *
 * @param initialNumber - Исходное число или строка, представляющая число.
 * @param maxPossibleInteger - Максимально возможная длина целой части числа.
 * @returns Строка, представляющая обрезанное число или исходное число в формате строки.
 */
const cutOffIntegerPart = (initialNumber: string, maxPossibleInteger: number): string => {
  const numberString = String(initialNumber);
  // выделяем целую и дробную часть строки так для числа 100.45 получим масси ['100.45', '100', '.45', ....]
  // а для целого числа 100 получим ['100', '100', undefined, ....]
  const [fullMatch, integerPart, decimalPointWithFraction = ''] = numberString.match(/(\d+)([.,]\d+)?/) || [];

  if (!fullMatch) {
    return numberString; // Возвращает строковое представление, если не найдено совпадений.
  }

  const maxIntegerLength = String(maxPossibleInteger).length;
  const truncatedIntegerPart = integerPart.slice(0, maxIntegerLength);

  return truncatedIntegerPart + decimalPointWithFraction;
};

/**
 * Обрезает число до допустимого максимального значения, основываясь на счете пользователя и максимальном лимите.
 * Если число превышает допустимое максимальное значение, оно обрезается функцией `cutOffIntegerPart`.
 *
 * @param initialNumber - Исходное число в формате строки, которое необходимо проверить и возможно обрезать.
 * @param userAccount - Текущий баланс счета пользователя.
 * @param maxLimit - Опциональный максимальный лимит. Если не задан, используется 0.
 * @returns Строка, представляющая исходное число, обрезанное до допустимого максимального значения, если применимо,
 *   иначе возвращается исходное число.
 */
export const trimValueToAcceptable = (
  initialNumber: string,
  userAccount: number,
  maxLimit: string | number | undefined,
): string => {
  // увеличиваем число на 1 разряд что бы пользователю отобразилась ошибка о максимальном лимите
  const possibleMaxAmount = Math.max(userAccount, Number(maxLimit || 0)) * 10;

  if (+initialNumber > possibleMaxAmount) {
    return cutOffIntegerPart(initialNumber, possibleMaxAmount);
  }

  return initialNumber;
};
